import { useState } from 'react'
import { GetDeviceMetricsParams } from '@/api/deviceApi'

export type TimeRangeConfig = {
  label: string
  durationMs: number
  agg: GetDeviceMetricsParams['agg']
  interval?: number
  tickFormatter?: (ts: number) => string
}

const formatTime = (ts: number) =>
  new Date(ts).toLocaleTimeString(navigator.language, {
    hour: '2-digit',
    minute: '2-digit'
  })

const formatDateTime = (ts: number) =>
  new Date(ts).toLocaleString(navigator.language, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  })

const formatDate = (ts: number) =>
  new Date(ts).toLocaleDateString(navigator.language, {
    day: 'numeric',
    month: 'short'
  })

const trim = (v: number) => (v % 1 === 0 ? v : v.toFixed(1))

export const formatPercent = (v: number) => `${trim(v)}%`
export const formatCelsius = (v: number) => `${trim(v)}°C`
export const formatWatts = (v: number) => `${trim(v)} W`
export const formatMegahertz = (v: number) => `${Math.round(v)} MHz`

// 1h uses raw data points; longer ranges aggregate to avoid noisy/dense charts
export const TIME_RANGES: TimeRangeConfig[] = [
  {
    label: '1h',
    durationMs: 60 * 60 * 1000,
    agg: 'NONE',
    tickFormatter: formatTime
  },
  {
    label: '6h',
    durationMs: 6 * 60 * 60 * 1000,
    agg: 'AVG',
    interval: 5 * 60 * 1000,
    tickFormatter: formatTime
  },
  {
    label: '24h',
    durationMs: 24 * 60 * 60 * 1000,
    agg: 'AVG',
    interval: 30 * 60 * 1000,
    tickFormatter: formatDateTime
  },
  {
    label: '7d',
    durationMs: 7 * 24 * 60 * 60 * 1000,
    agg: 'AVG',
    interval: 3 * 60 * 60 * 1000,
    tickFormatter: formatDate
  }
]

export const TIME_RANGE_OPTIONS = TIME_RANGES.map((r) => ({
  label: r.label,
  value: r.label
}))

export const useMetricTimeRange = () => {
  const [range, setRange] = useState(TIME_RANGES[0])
  const [endTs, setEndTs] = useState(() => Date.now())

  const selectRange = (label: string) => {
    const next = TIME_RANGES.find((r) => r.label === label)
    if (next) {
      setRange(next)
      setEndTs(Date.now())
    }
  }

  return { range, startTs: endTs - range.durationMs, endTs, selectRange }
}
